import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_ARK_TASK_IN_FLIGHT_ALLOWANCE,
  type OrgSkuTaskAdmissionOverride,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  deleteOrgSkuTaskAdmissionOverride,
  listOrgSkuTaskAdmissionOverrides,
  putOrgSkuTaskAdmissionOverride,
} from '../api/taskAdmission.ts';
import { QUOTA_TASK_ADMISSION_KEY } from '../api/useRoutingLoad.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { useToast } from './Toast.tsx';
import {
  Button,
  Chip,
  EmptyState,
  Input,
  Panel,
  Skeleton,
  Table,
  TBody,
  THead,
  Td,
  Th,
  Tr,
} from '../ui/index.ts';

function queryKey(orgId: string): readonly ['task-admission', string] {
  return ['task-admission', orgId] as const;
}

function formatMap(map: Readonly<Record<string, number>> | null): string {
  if (map === null) return '—';
  return Object.entries(map)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(', ');
}

function draftFromItem(item: OrgSkuTaskAdmissionOverride): Record<string, string> {
  const source = item.overlay_max_in_flight_by_tier_class ?? item.default_max_in_flight_by_tier_class;
  const draft: Record<string, string> = {};
  for (const key of Object.keys(item.default_max_in_flight_by_tier_class)) {
    const value = source[key] ?? item.default_max_in_flight_by_tier_class[key];
    draft[key] = typeof value === 'number' ? String(value) : '';
  }
  return draft;
}

function parseDraft(
  item: OrgSkuTaskAdmissionOverride,
  draft: Readonly<Record<string, string>>,
): { ok: true; overlay: Record<string, number> } | { ok: false } {
  const overlay: Record<string, number> = {};
  for (const key of Object.keys(item.default_max_in_flight_by_tier_class)) {
    const raw = draft[key];
    const allowance = raw === undefined ? Number.NaN : Number(raw);
    if (
      !Number.isSafeInteger(allowance) ||
      allowance < 1 ||
      allowance > MAX_ARK_TASK_IN_FLIGHT_ALLOWANCE
    ) {
      return { ok: false };
    }
    overlay[key] = allowance;
  }
  return { ok: true, overlay };
}

export function CustomerTaskAdmissionCard({
  orgId,
  testId,
  readOnly = false,
}: {
  readonly orgId: string;
  readonly testId: string;
  readonly readOnly?: boolean;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: queryKey(orgId),
    queryFn: ({ signal }) => listOrgSkuTaskAdmissionOverrides(orgId, fetchImpl, signal),
    enabled: orgId.length > 0,
  });
  const items = query.data?.items ?? [];
  const [drafts, setDrafts] = useState<Readonly<Record<string, Record<string, string>>>>({});

  useEffect(() => {
    if (query.data === undefined) return;
    const next: Record<string, Record<string, string>> = {};
    for (const item of query.data.items) {
      next[item.sku_id] = draftFromItem(item);
    }
    setDrafts(next);
  }, [query.data]);

  const putMut = useMutation({
    mutationFn: ({ skuId, overlay }: { skuId: string; overlay: Record<string, number> }) =>
      putOrgSkuTaskAdmissionOverride(
        orgId,
        skuId,
        { max_in_flight_by_tier_class: overlay },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKey(orgId) });
      void qc.invalidateQueries({ queryKey: QUOTA_TASK_ADMISSION_KEY });
      toast.success(t('customer_manager_360.task_admission.saved'));
    },
    onError: (cause) => {
      toast.error(cause instanceof OpsApiError ? cause.message : t('customer_manager_360.task_admission.save_error'));
    },
  });

  const clearMut = useMutation({
    mutationFn: (skuId: string) => deleteOrgSkuTaskAdmissionOverride(orgId, skuId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKey(orgId) });
      void qc.invalidateQueries({ queryKey: QUOTA_TASK_ADMISSION_KEY });
      toast.success(t('customer_manager_360.task_admission.cleared'));
    },
    onError: (cause) => {
      toast.error(cause instanceof OpsApiError ? cause.message : t('customer_manager_360.task_admission.clear_error'));
    },
  });

  const busySku = putMut.isPending
    ? putMut.variables?.skuId
    : clearMut.isPending
      ? clearMut.variables
      : null;

  const keysBySku = useMemo(() => {
    const map = new Map<string, readonly string[]>();
    for (const item of items) {
      map.set(item.sku_id, Object.keys(item.default_max_in_flight_by_tier_class));
    }
    return map;
  }, [items]);

  if (query.isLoading) {
    return (
      <Panel caption={t('customer_manager_360.task_admission.caption')} padded testId={testId}>
        <div data-testid={`${testId}-loading`} className="flex flex-col gap-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-2/3" />
        </div>
      </Panel>
    );
  }

  if (query.data === undefined) {
    return (
      <Panel caption={t('customer_manager_360.task_admission.caption')} padded testId={testId}>
        <div
          data-testid={`${testId}-error`}
          role="alert"
          className="flex flex-col items-start gap-3 rounded-lg border border-bad-line bg-bad-dim px-4 py-3"
        >
          <p className="m-0 text-dense text-bad-bright">
            {t('customer_manager_360.task_admission.error')}
          </p>
          <Button
            variant="secondary"
            size="sm"
            loading={query.isFetching}
            onClick={() => { void query.refetch(); }}
            data-testid={`${testId}-retry`}
          >
            {t('customer_manager_360.task_admission.retry')}
          </Button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      caption={t('customer_manager_360.task_admission.caption')}
      count={items.length}
      testId={testId}
      actions={
        readOnly ? (
          <Link
            to={`/quotas?org=${encodeURIComponent(orgId)}`}
            className="text-dense text-signal hover:text-signal-bright"
            data-testid={`${testId}-edit-link`}
          >
            {t('quotas.link_edit')}
          </Link>
        ) : undefined
      }
    >
      {items.length === 0 ? (
        <EmptyState
          title={t('customer_manager_360.task_admission.empty')}
          hint={t('customer_manager_360.task_admission.empty_hint')}
          testId={`${testId}-empty`}
        />
      ) : (
        <Table testId={`${testId}-table`}>
          <THead>
            <Tr interactive={false}>
              <Th>{t('customer_manager_360.task_admission.sku')}</Th>
              <Th>{t('customer_manager_360.task_admission.default')}</Th>
              <Th>{t('customer_manager_360.task_admission.overlay')}</Th>
              {readOnly ? null : <Th>{t('customer_manager_360.task_admission.actions')}</Th>}
            </Tr>
          </THead>
          <TBody>
            {items.map((item) => {
              const keys = keysBySku.get(item.sku_id) ?? [];
              const draft = drafts[item.sku_id] ?? draftFromItem(item);
              const busy = busySku === item.sku_id;
              return (
                <Tr key={item.sku_id} interactive={false} data-testid={`${testId}-row-${item.sku_id}`}>
                  <Td emphasis>
                    <div className="flex flex-col gap-1">
                      <span>{item.sku_name}</span>
                      <span className="font-mono text-micro text-paper-low">{item.sku_id}</span>
                    </div>
                  </Td>
                  <Td mono>{formatMap(item.default_max_in_flight_by_tier_class)}</Td>
                  <Td>
                    <div className="flex flex-col gap-2">
                      {item.overlay_max_in_flight_by_tier_class === null ? (
                        <Chip tone="neutral" lamp={false}>
                          {t('customer_manager_360.task_admission.no_overlay')}
                        </Chip>
                      ) : (
                        <Chip tone="signal" lamp={false}>
                          {formatMap(item.overlay_max_in_flight_by_tier_class)}
                        </Chip>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {readOnly ? null : keys.map((key) => (
                          <label
                            key={key}
                            className="flex items-center gap-1.5 text-micro text-paper-low"
                          >
                            <span className="font-mono">{key}</span>
                            <Input
                              type="number"
                              min={1}
                              max={MAX_ARK_TASK_IN_FLIGHT_ALLOWANCE}
                              step={1}
                              mono
                              wrapperClassName="w-20"
                              value={draft[key] ?? ''}
                              disabled={busy}
                              onChange={(event) => {
                                const value = event.target.value;
                                setDrafts((prev) => ({
                                  ...prev,
                                  [item.sku_id]: { ...(prev[item.sku_id] ?? draft), [key]: value },
                                }));
                              }}
                              data-testid={`${testId}-input-${item.sku_id}-${key}`}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  </Td>
                  {readOnly ? null : <Td>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        loading={putMut.isPending && busy}
                        disabled={busy}
                        onClick={() => {
                          const parsed = parseDraft(item, draft);
                          if (!parsed.ok) {
                            toast.error(t('customer_manager_360.task_admission.invalid'));
                            return;
                          }
                          putMut.mutate({ skuId: item.sku_id, overlay: parsed.overlay });
                        }}
                        data-testid={`${testId}-save-${item.sku_id}`}
                      >
                        {t('customer_manager_360.task_admission.save')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={clearMut.isPending && busy}
                        disabled={busy || item.overlay_max_in_flight_by_tier_class === null}
                        onClick={() => { clearMut.mutate(item.sku_id); }}
                        data-testid={`${testId}-clear-${item.sku_id}`}
                      >
                        {t('customer_manager_360.task_admission.clear')}
                      </Button>
                    </div>
                  </Td>}
                </Tr>
              );
            })}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}
